import { useState } from "react";
import { TellescopeModelFromName, useTellescopeInfiniteQuery, useTellescopeMutation } from "@tellescope/tanstack-query";
import { ConfirmDialog } from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { FileCard } from "./FileCard";
import { BlockPaginatedList } from "../BlockPaginatedList";
import { BlockEmptyState } from "../BlockEmptyState";

export interface FilesRecordsListProps {
  contact: TellescopeModelFromName<"endusers">;
}

export const FilesRecordsList = ({ contact }: FilesRecordsListProps) => {
  const { t } = useTranslation("contacts");
  const [fileToDelete, setFileToDelete] = useState<string | null>(null);

  const filesQuery = useTellescopeInfiniteQuery({
    model: "files",
    method: "getSome",
    request: {
      filter: {
        enduserId: contact.id,
      },
    },
    select: (data) => data.pages.flat(),
  });

  const deleteFileMutation = useTellescopeMutation({
    model: "files",
    method: "deleteOne",
    onSuccess: () => {
      setFileToDelete(null);
    },
  });

  return (
    <div className="flex-1 min-h-0 flex flex-col pr-2">
      {filesQuery.isLoading ? (
        <div className="flex flex-1 items-center justify-center">
          <BlockEmptyState>{t("Loading files...")}</BlockEmptyState>
        </div>
      ) : filesQuery.isError ? (
        <div className="flex flex-1 items-center justify-center">
          <BlockEmptyState>{t("Error loading files for contact.")}</BlockEmptyState>
        </div>
      ) : filesQuery.data?.length ? (
        <>
          <BlockPaginatedList
            className="flex flex-col"
            scrollbar="floating"
            fadeWhenClipped
            onBottomReached={() => filesQuery.fetchNextPage()}
          >
            {filesQuery.data.map((file) => (
              <FileCard
                key={file.id}
                name={file.name}
                type={file.type}
                size={file.size}
                deleteLabel={t("Delete")}
                onDelete={() => setFileToDelete(file.id)}
                className="mb-2"
              />
            ))}
          </BlockPaginatedList>

          <ConfirmDialog
            open={!!fileToDelete}
            onOpenChange={(open) => !open && setFileToDelete(null)}
            variant="destructive"
            title={t("Delete File")}
            description={t("Are you sure you want to delete this file? This action cannot be undone.")}
            confirmText={t("Delete")}
            onConfirm={async () => {
              if (fileToDelete) {
                await deleteFileMutation.mutateAsync(fileToDelete);
              }
            }}
          />
        </>
      ) : (
        <div className="flex flex-1 items-center justify-center">
          <BlockEmptyState>{t("No files for this contact.")}</BlockEmptyState>
        </div>
      )}
    </div>
  );
};
